import { Button } from '@/components/ui/button'
import { useAppUpdate } from '@/hooks/use-app-update'
import { Sparkles, X } from 'lucide-react'
import { useEffect, useRef } from 'react'

/**
 * Top-of-page banner shown to returning users after a new production deploy.
 * Title and description come from public/meta.json (edit it per release).
 */
export function UpdateBanner() {
  const { update, dismiss } = useAppUpdate()
  const bannerRef = useRef<HTMLDivElement>(null)

  // Push the app down by the banner height so it never covers the header
  useEffect(() => {
    if (!update) return
    const banner = bannerRef.current
    if (!banner) return

    const applyOffset = () => {
      document.body.style.paddingTop = `${banner.offsetHeight}px`
    }
    applyOffset()

    const observer = new ResizeObserver(applyOffset)
    observer.observe(banner)

    return () => {
      observer.disconnect()
      document.body.style.paddingTop = ''
    }
  }, [update])

  if (!update) return null

  return (
    <div
      ref={bannerRef}
      role='status'
      aria-live='polite'
      className='fixed top-0 inset-x-0 z-[999] border-b border-blue-200 dark:border-blue-800/50 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-indigo-950 shadow-md animate-in slide-in-from-top duration-300'
    >
      <div className='mx-auto flex max-w-7xl items-center gap-3 px-4 py-2.5 sm:px-6'>
        <div className='hidden sm:flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/40'>
          <Sparkles className='h-4 w-4 text-blue-600 dark:text-blue-400' />
        </div>

        <div className='min-w-0 flex-1'>
          <p className='text-sm font-semibold text-blue-900 dark:text-blue-100'>{update.title}</p>
          <p className='line-clamp-2 text-xs text-blue-700 dark:text-blue-300'>{update.description}</p>
        </div>

        <Button
          size='icon'
          variant='ghost'
          onClick={dismiss}
          aria-label='Dismiss update notification'
          className='h-8 w-8 shrink-0 text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-100'
        >
          <X className='h-4 w-4' />
        </Button>
      </div>
    </div>
  )
}
